import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { Link, Navigate, Route, Routes } from 'react-router-dom'
import { HostData } from '../Customer/Data'
import { HealthCheckBrief } from './HealthCheckBrief'
import { FleetMakeupBrief } from './FleetMakeupBrief'
import {
  BOARD_COLUMNS,
  BoardColumn,
  isLifecycleStage,
  lifecycleStageFor,
  LifecycleStage,
  stageOrDefault,
} from './lifecycle'
import styles from './BriefsHub.module.css'
import healthCheckThumb from '../../assets/img/brief-thumb-health-check.svg'
import fleetMakeupThumb from '../../assets/img/brief-thumb-fleet-makeup.svg'

interface Props {
  teamID: string
  host: HostData
}

const INTRO_TIP_MS = 9000

/* Points out the brief that's ready to open, and what it's for, when the tab loads */
const AvailableBriefTip: FunctionComponent = () => {
  const [phase, setPhase] = useState<'open' | 'leaving' | 'gone'>('open')
  useEffect(() => {
    const t = window.setTimeout(() => setPhase(prev => (prev === 'open' ? 'leaving' : prev)), INTRO_TIP_MS)
    return () => window.clearTimeout(t)
  }, [])
  if (phase === 'gone') return null
  return (
    <div
      className={`${styles.availableTip}${phase === 'leaving' ? ` ${styles.availableTipLeaving}` : ''}`}
      role='status'
      onAnimationEnd={e => {
        if (phase === 'leaving' && e.target === e.currentTarget) setPhase('gone')
      }}
    >
      <button
        type='button'
        className={styles.availableTipClose}
        aria-label='Dismiss'
        onClick={() => setPhase('leaving')}
      >
        ×
      </button>
      <strong>Health check is ready</strong>
      <span>
        See fleet makeup, print volume, feature use and renewal risk in one view, with AI notes and Mission Control to
        plan your next conversation.
      </span>
    </div>
  )
}

/* the customer's lifecycle stage, as set on the Intel tab (undefined when it isn't set), and whether it has loaded */
const useLifecycleStage = (teamID: string, customerID: string): { stage?: LifecycleStage; loaded: boolean } => {
  const [stage, setStage] = useState<LifecycleStage>()
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    setLoaded(false)
    const controller = new AbortController()
    const request = axios.get<{ lifecycleStage?: string }>(`/api/teams/${teamID}/customers/${customerID}/profile`, {
      signal: controller.signal,
    })
    if (request && typeof request.then === 'function') {
      request
        .then(response => {
          const value = response?.data?.lifecycleStage
          setStage(isLifecycleStage(value) ? value : undefined)
          setLoaded(true)
        })
        .catch(err => {
          if (!axios.isCancel(err)) setLoaded(true) // no stage: the board shows its default
        })
    } else {
      setLoaded(true)
    }
    return () => controller.abort()
  }, [teamID, customerID])
  return { stage, loaded }
}

const BriefsList: FunctionComponent<{ teamID: string; customerID: string }> = ({ teamID, customerID }) => {
  // the stage set on the Intel tab is the current stage (Serve & upsell until one is set); it can span columns
  const lifecycle = useLifecycleStage(teamID, customerID)
  const stage = stageOrDefault(lifecycle.stage)
  const current: BoardColumn[] = lifecycleStageFor(stage)?.columns ?? []
  const first = current[0]
  // the Expired column only appears for an expired customer
  const expired = stage === 'expired'
  const columns = BOARD_COLUMNS.filter(column => expired || column.value !== 'expired')

  // On narrow screens the lifecycle scrolls sideways; start it at the current stage rather than Trial.
  const processRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const strip = processRef.current
    if (!strip || strip.scrollWidth <= strip.clientWidth) return
    const col = strip.querySelector(`header[data-content="${first}"]`)?.closest('.col')
    if (!col) return
    strip.scrollLeft = col.getBoundingClientRect().left - strip.getBoundingClientRect().left - 8
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when the stage arrives
  }, [first, lifecycle.loaded])

  return (
    <div>
      <header className={styles.header}>
        <h2>
          Briefs by lifecycle <span className={styles.comingSoon}>More briefs coming soon</span>
        </h2>
      </header>
      <p>Curated, point-in-time views of the customer's PaperCut deployment across their lifecycle.</p>

      {/* the board waits for the stage, so it arrives with the right columns and Current stage (no late changes) */}
      {lifecycle.loaded && (
        <div data-group='process' ref={processRef}>
          {/* Stage Headers Row: the current stage in colour, flagged "Current stage" */}
          <div className='row flex'>
            {columns.map(({ value, label }) => {
              const isCurrent = current.includes(value)
              // the flag spans every column of the current stage (Serve & upsell has two)
              const span = current.length === 1 ? styles.currentStageSingle : ''
              return (
                <div key={value} className={isCurrent ? 'col relative' : 'col'}>
                  {value === first && (
                    <div className={`tip visible center fp ${span}`} data-testid='current-stage'>
                      <strong>Current stage</strong>
                    </div>
                  )}
                  <header data-content={value} data-highlight={isCurrent ? '' : undefined}>
                    {label}
                  </header>
                </div>
              )
            })}
          </div>

          {/* Content & Grid Overlay Area */}
          <div className='relative'>
            <div className='row flex' data-content='grid-overlay'>
              {columns.map(({ value }) => (
                <div key={value} className='col'></div>
              ))}
            </div>

            <div className='row flex'>
              {/* Column: Trial */}
              <div className='col' data-content='trial'>
                <div className='wallet fp'>
                  <div className='list condensed stacked' data-list='briefings'>
                    <ul className='no-margin-bottom'>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Industry benchmark</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Print security assessment</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Volume forecasting/trends</div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <span className='leather'>
                    <h5>Proposal &amp; planning</h5>
                  </span>
                </div>
              </div>

              {/* Column: Onboarding */}
              <div className='col' data-content='onboarding'>
                <div className='wallet fp'>
                  <div className='list condensed stacked' data-list='briefings'>
                    <ul className='no-margin-bottom'>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Feature utilization</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Environment, social & governance</div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <span className='leather'>
                    <h5>Feature usage</h5>
                  </span>
                </div>
                <div className='wallet fp'>
                  <div className='list condensed stacked' data-list='briefings'>
                    <ul className='no-margin-bottom'>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>System stability</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Business review</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Printer security checklist</div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <span className='leather'>
                    <h5>System usage</h5>
                  </span>
                </div>
              </div>

              {/* Column: Serve */}
              <div className='col' data-content='serve'>
                <div className='wallet fp'>
                  <div className='list condensed stacked' data-list='briefings'>
                    <ul className='no-margin-bottom'>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Business review</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Feature utilization</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Volume forecasting/trends</div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <span className='leather'>
                    <h5>Activity overview</h5>
                  </span>
                </div>
                <div className='wallet fp'>
                  <div className='list condensed stacked' data-list='briefings'>
                    <ul className='no-margin-bottom'>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Print security checkup</div>
                          </div>
                        </div>
                      </li>
                      <li data-highlight='' className={styles.tipAnchor}>
                        <AvailableBriefTip />
                        <Link to='health-check' className='row row-link' data-brief='Health check'>
                          <img src={healthCheckThumb} alt='' className={styles.briefWatermark} />
                          <div className='col title'>
                            <div className='ellipsis'>Health check</div>
                          </div>
                        </Link>
                      </li>
                    </ul>
                  </div>
                  <span className='leather'>
                    <h5>System health</h5>
                  </span>
                </div>
              </div>

              {/* Column: Upsell */}
              <div className='col' data-content='upsell'>
                <div className='wallet fp'>
                  <div className='list condensed stacked' data-list='briefings'>
                    <ul className='no-margin-bottom'>
                      <li data-highlight=''>
                        <Link to='fleet-makeup' className='row row-link' data-brief='Fleet makeup'>
                          <img src={fleetMakeupThumb} alt='' className={styles.briefWatermark} />
                          <div className='col title'>
                            <div className='ellipsis'>Fleet makeup</div>
                          </div>
                        </Link>
                      </li>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Additional embeddeds & licenses</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>License & feature expansion</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Benchmark & compare</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Business review</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Volume forecasting/trends</div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <span className='leather'>
                    <h5>Opportunities</h5>
                  </span>
                </div>
              </div>

              {/* Column: Retention */}
              <div className='col' data-content='retention'>
                <div className='wallet fp'>
                  <div className='list condensed stacked' data-list='briefings'>
                    <ul className='no-margin-bottom'>
                      <li>
                        <div className='row row-link has-tip'>
                          <span className='tip center'>Coming soon</span>
                          <div className='col title'>
                            <div className='ellipsis'>Business review</div>
                          </div>
                        </div>
                      </li>
                      <li data-highlight=''>
                        <Link to='health-check' className='row row-link' data-brief='Health check'>
                          <img src={healthCheckThumb} alt='' className={styles.briefWatermark} />
                          <div className='col title'>
                            <div className='ellipsis'>Health check</div>
                          </div>
                        </Link>
                      </li>
                    </ul>
                  </div>
                  <span className='leather'>
                    <h5>Renewal maximizer</h5>
                  </span>
                </div>
              </div>

              {/* Column: Expired (only for an expired customer) */}
              {expired && (
                <div className={'col'} data-content='expired'>
                  <div className='wallet fp'>
                    <div className='list condensed stacked' data-list='briefings'>
                      <ul className='no-margin-bottom'>
                        <li>
                          <div className='row row-link has-tip'>
                            <span className='tip center'>Coming soon</span>
                            <div className='col title'>
                              <div className='ellipsis'>Win-back proposal</div>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </div>
                    <span className='leather'>
                      <h5>Win back</h5>
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export const BriefsHub: FunctionComponent<Props> = ({ teamID, host }) => (
  <Routes>
    <Route index element={<BriefsList teamID={teamID} customerID={host.id} />} />
    <Route path='health-check' element={<HealthCheckBrief teamID={teamID} host={host} />} />
    <Route path='fleet-makeup' element={<FleetMakeupBrief teamID={teamID} host={host} />} />
    <Route path='*' element={<Navigate to='.' replace />} />
  </Routes>
)
